import * as React from 'react';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { ArrowDownIcon } from '../../assets/icons/ArrowDownIcon';
import styled from 'styled-components';
import Button from '../button/Button';
import { AddIcon } from '../../assets/icons/AddIcon';

interface Props {
  options: { label: string; onClick: () => void }[];
  variant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'action';
}

export default function SplitButton(props: Props) {
  const { options, variant = 'primary' } = props;
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleOnClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLLIElement, MouseEvent>,
    optionId: number
  ) => {
    e.stopPropagation();
    options[optionId].onClick();
  };

  return (
    <>
      <ButtonGroup sx={{ boxShadow: 'none' }} variant="contained" ref={anchorRef} aria-label="split button">
        <StyledLeftButton onClick={(e) => handleOnClick(e, 0)} variant={variant} data-testid="split-btn-left">
          <AddIcon size="20px" />
          {options[0].label}
        </StyledLeftButton>
        <StyledRightButton
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
          variant={variant}
        >
          <ArrowDownIcon size="16px" />
        </StyledRightButton>
      </ButtonGroup>
      <Popper
        style={{
          zIndex: 1
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options
                    .filter((_, i) => i !== 0)
                    .map((option, i) => (
                      <MenuItem key={option.label} onClick={(e) => handleOnClick(e, i + 1)}>
                        {option.label}
                      </MenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

const StyledLeftButton = styled(Button)`
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
`;

const StyledRightButton = styled(Button)`
  border-left-color: ${(props) => (props.variant === 'primary' ? props.theme.palette.grey.black60 : '')};
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  padding: 10px;
`;
